import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { BrowserRouter, MemoryRouter, Route, Routes, useLocation, useNavigate } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BRAND_DEFAULT, SprocketThemeProvider, ToastProvider } from '@platform/design-system';
import {
  ApiProvider,
  DocumentsDueGate,
  RequireSession,
  RequireTenant,
  SessionProvider,
  useSession,
  type AppPrincipal,
  type SessionHooks,
  type SignInReason,
} from '@platform/app-kit';
import { shouldReadMe } from './after-sign-in.ts';
import type { Runtime } from './bootstrap.ts';
import { AccountPage } from './pages/account-page.tsx';
import { ForgotPasswordPage } from './pages/forgot-password-page.tsx';
import { HomePage } from './pages/home-page.tsx';
import { LoginPage } from './pages/login-page.tsx';
import { MockCheckoutPage, MockPortalPage } from './pages/mock-checkout-page.tsx';
import { NotFoundPage } from './pages/not-found-page.tsx';
import { SignupCompletePage } from './pages/signup-complete-page.tsx';
import { SignupPage, VerifyEmailPage } from './pages/signup-page.tsx';
import { SubscribePage } from './pages/subscribe-page.tsx';
import { AUTH_FRAME } from './shell/brand.ts';
import { PersonalShell } from './shell/personal-shell.tsx';

function ApiBoundary({ runtime, children }: { runtime: Runtime; children: ReactNode }) {
  const { getAccessToken, getPrincipal } = useSession();
  const api = useMemo(
    () =>
      runtime.createApi({
        getAccessToken,
        getPrincipal,
        getTenantId: () => getPrincipal()?.tenantId ?? null,
      }),
    [runtime, getAccessToken, getPrincipal],
  );
  return <ApiProvider value={api}>{children}</ApiProvider>;
}

/** The signed-in app, behind the documents the person still has to accept. */
function GatedShell() {
  const navigate = useNavigate();
  return (
    <DocumentsDueGate
      frame={AUTH_FRAME}
      onSignedOut={() => void navigate('/login', { replace: true })}
    >
      <PersonalShell />
    </DocumentsDueGate>
  );
}

export function AppRoutes({ runtime }: { runtime: Runtime }) {
  const mock = runtime.config.auth.mode === 'mock';
  const open = runtime.config.controlPlane.platform;
  // A fresh login page per navigation (its notice and email come from the query string).
  const { key } = useLocation();
  return (
    <Routes>
      <Route path="/login" element={<LoginPage key={key} />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/signup" element={<SignupPage open={open} />} />
      <Route path="/signup/verify" element={<VerifyEmailPage />} />
      <Route path="/signup/complete" element={<SignupCompletePage />} />
      {mock && <Route path="/__mock/pay" element={<MockCheckoutPage runtime={runtime} />} />}
      {mock && <Route path="/__mock/portal" element={<MockPortalPage />} />}
      <Route
        path="/subscribe"
        element={
          <RequireSession>
            <SubscribePage open={open} />
          </RequireSession>
        }
      />
      <Route
        element={
          <RequireSession>
            {/* A tenant of one: no picker — no plan yet means choosing one. */}
            <RequireTenant pickerPath="/subscribe">
              <GatedShell />
            </RequireTenant>
          </RequireSession>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export interface AppProps {
  runtime: Runtime;
  /** Tests render inside a MemoryRouter at a given path. */
  initialPath?: string;
  queryClient?: QueryClient;
}

export function App({ runtime, initialPath, queryClient }: AppProps) {
  const [client] = useState(
    () =>
      queryClient ??
      new QueryClient({ defaultOptions: { queries: { retry: false, staleTime: 5_000 } } }),
  );

  // Identity rule 4: GET /me with X-Tenant-Id right after every sign-in, restore and refresh
  // once the person has their tenant. Before that (no plan yet) there is nothing to read.
  const afterSignIn = useCallback(
    async (principal: AppPrincipal, reason: SignInReason): Promise<AppPrincipal> => {
      if (!principal.tenantId) return principal;
      // Back from the payment page the stored tokens are the old ones, from before the plan
      // existed: asking `/me` with them is refused. The wait page refreshes the session first and
      // the refresh runs this hook again with the new tokens (#17).
      if (!shouldReadMe(reason, window.location.pathname)) return principal;
      const api = runtime.createApi({
        getAccessToken: () => runtime.adapter.getAccessToken(),
        getPrincipal: () => principal,
        getTenantId: () => principal.tenantId ?? null,
      });
      await api.identity.Self_get();
      return principal;
    },
    [runtime],
  );
  const hooks = useMemo<SessionHooks>(() => ({ afterSignIn }), [afterSignIn]);

  const Router = initialPath ? MemoryRouter : BrowserRouter;
  const routerProps = initialPath ? { initialEntries: [initialPath] } : {};
  return (
    <QueryClientProvider client={client}>
      <SessionProvider adapter={runtime.adapter} hooks={hooks}>
        {/* The design system's default theme until the product has its own brand (#26). */}
        <SprocketThemeProvider brand={BRAND_DEFAULT}>
          <ToastProvider>
            <ApiBoundary runtime={runtime}>
              <Router {...routerProps}>
                <AppRoutes runtime={runtime} />
              </Router>
            </ApiBoundary>
          </ToastProvider>
        </SprocketThemeProvider>
      </SessionProvider>
    </QueryClientProvider>
  );
}
